import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  XCircle,
  RefreshCw,
  Phone,
  Mail,
  Building,
  User,
  AlertTriangle,
  CreditCard,
  Loader2
} from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { mapHyperswitchError } from '../lib/hyperswitchErrors';

interface PaymentFailureData {
  orderId: string;
  paymentLinkId: string;
  patientName: string;
  clinicName: string;
  clinicPhone?: string;
  clinicEmail?: string;
  totalAmount: number;
  currency: string;
  failureReason: string;
  errorCode?: string;
  canRetry: boolean;
  retryLimit: number;
  attemptCount: number;
  failedAt: string;
}

const commonFailureReasons = {
  insufficient_funds: {
    title: 'Insufficient Funds',
    description: 'Your card was declined due to insufficient funds.',
    suggestion: 'Please check your account balance or try a different payment method.'
  },
  card_declined: {
    title: 'Card Declined',
    description: 'Your card was declined by your bank.',
    suggestion: 'Please contact your bank or try a different payment method.'
  },
  expired_card: {
    title: 'Card Expired',
    description: 'The card you tried to use has expired.',
    suggestion: 'Please update your card information or use a different payment method.'
  },
  invalid_cvc: {
    title: 'Invalid Security Code',
    description: 'The security code (CVC) you entered is incorrect.',
    suggestion: 'Please check the 3-digit code on the back of your card and try again.'
  },
  processing_error: {
    title: 'Processing Error',
    description: 'There was a temporary issue processing your payment.',
    suggestion: 'This is usually temporary. Please try again in a few minutes.'
  },
  network_error: {
    title: 'Network Error',
    description: 'There was a connection issue while processing your payment.',
    suggestion: 'Please check your internet connection and try again.'
  }
};

export const PaymentFailure: React.FC = () => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState<PaymentFailureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const loadPaymentFailure = async () => {
      if (!paymentLinkId) {
        setError('Invalid payment reference');
        setLoading(false);
        return;
      }

      try {
        // TODO: Replace with actual API call
        const response = await fetch(`/api/payments/failure/${paymentLinkId}`);
        
        if (!response.ok) {
          throw new Error('Payment information not found');
        }

        const data = await response.json();
        setPaymentData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };

    loadPaymentFailure();
  }, [paymentLinkId]);

  const handleRetryPayment = async () => {
    if (!paymentData?.canRetry) return;

    setRetrying(true);
    try {
      // Navigate back to payment page for retry
      navigate(`/payment/${paymentData.paymentLinkId}`);
    } catch (err) {
      console.error('Error retrying payment:', err);
      setRetrying(false);
    }
  };

  const getFailureInfo = (reason: string) => {
    // First try the local mapping for backward compatibility
    const key = reason.toLowerCase().replace(/\s+/g, '_') as keyof typeof commonFailureReasons;
    if (commonFailureReasons[key]) {
      return commonFailureReasons[key];
    }

    // Fall back to centralized Hyperswitch error mapping
    const hyperswitchError = mapHyperswitchError(reason, reason);
    return {
      title: 'Payment Failed',
      description: hyperswitchError.userMessage,
      suggestion: hyperswitchError.suggestion
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (error || !paymentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Information Unavailable</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const failureInfo = getFailureInfo(paymentData.failureReason);
  const formattedDate = formatDateTime(paymentData.failedAt, { month: 'long' });

  const attemptsRemaining = paymentData.retryLimit - paymentData.attemptCount;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Failed</h1>
            <p className="text-gray-600">Your prescription payment could not be processed</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Failure Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-red-900">{failureInfo.title}</h2>
              <p className="text-red-800 mt-1 mb-3">{failureInfo.description}</p>
              <p className="text-red-700 text-sm font-medium">{failureInfo.suggestion}</p>
              
              {paymentData.errorCode && (
                <div className="mt-3 pt-3 border-t border-red-200">
                  <p className="text-sm text-red-700">
                    Error Code: <span className="font-mono">{paymentData.errorCode}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Payment Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
            
            <div className="space-y-4">
              {/* Payment Info */}
              <div className="grid grid-cols-1 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Amount</p>
                  <p className="font-medium text-gray-900">
                    ${paymentData.totalAmount.toFixed(2)} {paymentData.currency}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Failed at</p>
                  <p className="font-medium text-gray-900">{formattedDate}</p>
                </div>
                <div>
                  <p className="text-gray-600">Order ID</p>
                  <p className="font-medium text-gray-900 font-mono">{paymentData.orderId}</p>
                </div>
              </div>

              {/* Patient & Clinic Info */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Patient</p>
                    <p className="font-medium text-gray-900">{paymentData.patientName}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Building className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Prescribed by</p>
                    <p className="font-medium text-gray-900">{paymentData.clinicName}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">What can you do?</h2>
            
            <div className="space-y-4">
              {/* Retry Option */}
              {paymentData.canRetry && attemptsRemaining > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <RefreshCw className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-medium text-blue-900">Try Again</h3>
                      <p className="text-blue-800 text-sm mt-1">
                        You can retry your payment. You have {attemptsRemaining} attempt
                        {attemptsRemaining !== 1 ? 's' : ''} remaining.
                      </p>
                      <button
                        onClick={handleRetryPayment}
                        disabled={retrying}
                        className={`mt-3 px-4 py-2 rounded-md font-medium transition-colors ${
                          retrying
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {retrying ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                            Redirecting...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 inline" />
                            Retry Payment
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Retry Limit Reached */}
              {!paymentData.canRetry || attemptsRemaining <= 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-yellow-900">Retry Limit Reached</h3>
                      <p className="text-yellow-800 text-sm mt-1">
                        You have reached the maximum number of payment attempts. 
                        Please contact the clinic for assistance.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Common Solutions */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Common solutions:</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start space-x-2">
                    <CreditCard className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span>Try a different payment method or card</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Phone className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span>Contact your bank to authorize the payment</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <RefreshCw className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                    <span>Wait a few minutes and try again</span>
                  </li>
                </ul>
              </div>

              {/* Contact Options */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">Need help?</h3>
                <div className="space-y-2">
                  {paymentData.clinicPhone && (
                    <a
                      href={`tel:${paymentData.clinicPhone}`}
                      className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call {paymentData.clinicName}
                    </a>
                  )}
                  
                  {paymentData.clinicEmail && (
                    <a
                      href={`mailto:${paymentData.clinicEmail}?subject=Payment%20Issue%20-%20Order%20${paymentData.orderId}`}
                      className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email {paymentData.clinicName}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center border-t border-gray-200 pt-8">
          <p className="text-sm text-gray-500 mb-2">
            Your prescription order has been saved and is awaiting payment.
          </p>
          <p className="text-sm text-gray-500">
            Questions? Contact {paymentData.clinicName}
            {paymentData.clinicPhone && (
              <>
                {' '}at <a href={`tel:${paymentData.clinicPhone}`} className="text-blue-600 hover:underline">
                  {paymentData.clinicPhone}
                </a>
              </>
            )}
            {paymentData.clinicEmail && (
              <>
                {' '}or <a href={`mailto:${paymentData.clinicEmail}`} className="text-blue-600 hover:underline">
                  {paymentData.clinicEmail}
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
